import clsx from 'clsx';
import React, { ReactNode, useRef, useState, useEffect } from 'react';
import ClockIcon from '../../assets/icons/clock-icon.svg?react';
import { useClickOutside } from '../../utils/clickOutSide';

interface TimePickerProps {
  otherClasses?: {
    container?: string;
    input?: string;
    label?: string;
    dropdownMainContainer?: string;
  };
  label?: string | ReactNode;
  name: string;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessage?: string;
  disabled?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  showSeconds?: boolean;
}

const convertTo12Hour = (time24: string): { hour: number; minute: number; second: number; ampm: 'AM' | 'PM' } => {
  if (!time24 || time24 === '') {
    const now = new Date();
    return {
      hour: now.getHours() % 12 || 12,
      minute: now.getMinutes(),
      second: now.getSeconds(),
      ampm: now.getHours() >= 12 ? 'PM' : 'AM',
    };
  }

  const [hours, minutes, seconds] = time24.split(':').map(Number);
  const hour24 = hours || 0;
  const hour12 = hour24 % 12 || 12;
  const ampm: 'AM' | 'PM' = hour24 >= 12 ? 'PM' : 'AM';

  return {
    hour: hour12,
    minute: minutes || 0,
    second: seconds || 0,
    ampm,
  };
};

const convertTo24Hour = (hour: number, minute: number, second: number, ampm: 'AM' | 'PM'): string => {
  let hour24 = hour;
  if (ampm === 'PM' && hour !== 12) {
    hour24 = hour + 12;
  } else if (ampm === 'AM' && hour === 12) {
    hour24 = 0;
  }

  const h = hour24.toString().padStart(2, '0');
  const m = minute.toString().padStart(2, '0');
  const s = second.toString().padStart(2, '0');

  return `${h}:${m}:${s}`;
};

const formatTimeDisplay = (hour: number, minute: number, second: number, ampm: 'AM' | 'PM', showSeconds: boolean): string => {
  const h = hour.toString().padStart(2, '0');
  const m = minute.toString().padStart(2, '0');
  const s = second.toString().padStart(2, '0');
  return showSeconds ? `${h}:${m}:${s} ${ampm}` : `${h}:${m} ${ampm}`;
};

const TimePicker: React.FC<TimePickerProps> = ({
  otherClasses,
  label,
  name,
  placeholder,
  value,
  onChange,
  errorMessage,
  disabled,
  onBlur,
  showSeconds = false,
}) => {
  const [toggle, setToggle] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside({ ref, setToggle });

  const time12 = convertTo12Hour(value);
  const [selectedHour, setSelectedHour] = useState(time12.hour);
  const [selectedMinute, setSelectedMinute] = useState(time12.minute);
  const [selectedSecond, setSelectedSecond] = useState(time12.second);
  const [selectedAmPm, setSelectedAmPm] = useState<'AM' | 'PM'>(time12.ampm);

  useEffect(() => {
    const time12 = convertTo12Hour(value);
    setSelectedHour(time12.hour);
    setSelectedMinute(time12.minute);
    setSelectedSecond(time12.second);
    setSelectedAmPm(time12.ampm);
  }, [value]);

  useEffect(() => {
    if (toggle && ref.current) {
      const container = ref.current as HTMLElement;
      const rect = container.getBoundingClientRect();
      const dropdownHeight = 280; // Approximate dropdown height
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setOpenAbove(true);
      } else {
        setOpenAbove(false);
      }
    }
  }, [toggle]);

  const timePickerClasses = clsx(
    otherClasses?.container,
    'flex flex-col gap-2.5 items-start relative w-fit',
  );

  const inputClasses = clsx(
    otherClasses?.input,
    'w-full h-60 global-transition rounded-10 placeholder:text-neutral-tones-text/50 text-neutral-tones-text ring -tracking-form-input p-5 text-base font-normal font-inter-variable outline-none disabled:ring-neutral-tones-text/35 disabled:placeholder:text-neutral-tones-text/35 disabled:text-neutral-tones-text/35 disabled:cursor-not-allowed cursor-pointer',
    'pr-14 pl-5',
    errorMessage !== ''
      ? 'ring-brand-posh-pink ring-2'
      : 'ring-neutral-tones-text/50 focus:ring-2 focus:ring-brand-byzantine',
  );

  const labelClasses = clsx(
    otherClasses?.label,
    'text-form-label font-bold -tracking-form-label font-inter-variable',
    disabled
      ? 'text-neutral-tones-text/35 cursor-not-allowed'
      : 'text-neutral-tones-text',
  );

  const handleTimeChange = (hour: number, minute: number, second: number, ampm: 'AM' | 'PM') => {
    const time24 = convertTo24Hour(hour, minute, second, ampm);
    const syntheticEvent = {
      target: {
        name,
        value: time24,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
  };

  const handleHourChange = (hour: number) => {
    setSelectedHour(hour);
    handleTimeChange(hour, selectedMinute, selectedSecond, selectedAmPm);
  };

  const handleMinuteChange = (minute: number) => {
    setSelectedMinute(minute);
    handleTimeChange(selectedHour, minute, selectedSecond, selectedAmPm);
  };

  const handleSecondChange = (second: number) => {
    setSelectedSecond(second);
    handleTimeChange(selectedHour, selectedMinute, second, selectedAmPm);
  };

  const handleAmPmChange = (ampm: 'AM' | 'PM') => {
    setSelectedAmPm(ampm);
    handleTimeChange(selectedHour, selectedMinute, selectedSecond, ampm);
  };

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  const seconds = Array.from({ length: 60 }, (_, i) => i);

  const displayValue = value
    ? formatTimeDisplay(selectedHour, selectedMinute, selectedSecond, selectedAmPm, showSeconds)
    : formatTimeDisplay(time12.hour, time12.minute, time12.second, time12.ampm, showSeconds);

  return (
    <div ref={ref} className={timePickerClasses} data-testid="time-picker">
      {label && (
        <label htmlFor={name} className={labelClasses}>
          {label}
        </label>
      )}
      <input
        disabled={disabled}
        id={name}
        type="text"
        name={name}
        value={displayValue}
        placeholder={placeholder || 'Select time'}
        onClick={() => !disabled && setToggle(!toggle)}
        onBlur={onBlur}
        readOnly
        className={inputClasses}
      />
      <button
        type="button"
        className="absolute top-[64%] right-5 -translate-y-2/4 cursor-pointer"
        onClick={() => !disabled && setToggle(!toggle)}
        disabled={disabled}
      >
        <ClockIcon className="[&>g>path]:fill-neutral-tones-text" />
      </button>
      {toggle && !disabled && (
        <div
          ref={dropdownRef}
          className={clsx(
            'bg-light-translucent-background dark:bg-dark-translucent-background rounded-10 shadow-button-box-shadow absolute left-0 z-10 p-6',
            otherClasses?.dropdownMainContainer,
            openAbove
              ? label
                ? 'bottom-24'
                : 'bottom-18'
              : label
                ? 'top-24'
                : 'top-18',
          )}
        >
          <div className="flex items-start gap-4 w-full">
            <div className="flex flex-col flex-1">
              <label className="text-xs font-bold font-inter-variable text-neutral-tones-text/50 mb-2">
                Hour
              </label>
              <div className="h-[200px] overflow-y-auto global-scrollbar-style border border-neutral-tones-text/20 rounded-md w-full">
                {hours.map((hour) => (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => handleHourChange(hour)}
                    className={clsx(
                      'w-full px-3 py-2 text-center font-inter-variable text-sm cursor-pointer global-transition',
                      selectedHour === hour
                        ? 'bg-brand-byzantine text-white font-bold'
                        : 'hover:bg-neutral-tones-text/10 text-neutral-tones-text',
                    )}
                  >
                    {hour.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col flex-1">
              <label className="text-xs font-bold font-inter-variable text-neutral-tones-text/50 mb-2">
                Minute
              </label>
              <div className="h-[200px] overflow-y-auto global-scrollbar-style border border-neutral-tones-text/20 rounded-md w-full">
                {minutes.map((minute) => (
                  <button
                    key={minute}
                    type="button"
                    onClick={() => handleMinuteChange(minute)}
                    className={clsx(
                      'w-full px-3 py-2 text-center font-inter-variable text-sm cursor-pointer global-transition',
                      selectedMinute === minute
                        ? 'bg-brand-byzantine text-white font-bold'
                        : 'hover:bg-neutral-tones-text/10 text-neutral-tones-text',
                    )}
                  >
                    {minute.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>

            {showSeconds && (
              <div className="flex flex-col flex-1">
                <label className="text-xs font-bold font-inter-variable text-neutral-tones-text/50 mb-2">
                  Second
                </label>
                <div className="h-[200px] overflow-y-auto global-scrollbar-style border border-neutral-tones-text/20 rounded-md w-full">
                  {seconds.map((second) => (
                    <button
                      key={second}
                      type="button"
                      onClick={() => handleSecondChange(second)}
                      className={clsx(
                        'w-full px-3 py-2 text-center font-inter-variable text-sm cursor-pointer global-transition',
                        selectedSecond === second
                          ? 'bg-brand-byzantine text-white font-bold'
                          : 'hover:bg-neutral-tones-text/10 text-neutral-tones-text',
                      )}
                    >
                      {second.toString().padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col flex-1">
              <label className="text-xs font-bold font-inter-variable text-neutral-tones-text/50 mb-2">
                Period
              </label>
              <div className="h-[200px] overflow-y-auto global-scrollbar-style border border-neutral-tones-text/20 rounded-md w-full">
                {(['AM', 'PM'] as const).map((ampm) => (
                  <button
                    key={ampm}
                    type="button"
                    onClick={() => handleAmPmChange(ampm)}
                    className={clsx(
                      'w-full px-3 py-2 text-center font-inter-variable text-sm cursor-pointer global-transition',
                      selectedAmPm === ampm
                        ? 'bg-brand-byzantine text-white font-bold'
                        : 'hover:bg-neutral-tones-text/10 text-neutral-tones-text',
                    )}
                  >
                    {ampm}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {errorMessage !== '' && (
        <span className="font-inter-variable text-brand-posh-pink absolute -bottom-5 left-0 text-xs font-normal">
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export default TimePicker;
